/**
 * Serviço para Web Push: obter chave VAPID, inscrever e enviar subscription ao back-end.
 * Usado após login para habilitar notificações no dispositivo (PWA).
 */

import { authHeader } from '@/helpers/easyindustriaapi/auth-header';

const baseApiUrl = process.env.VUE_APP_BASEURI + '/api/';

/**
 * Converte a chave pública VAPID (base64url) para Uint8Array para pushManager.subscribe().
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Obtém a chave pública VAPID do back-end.
 * @returns {Promise<Uint8Array>}
 */
export async function getVapidPublicKey() {
  const headers = authHeader();
  const res = await fetch(`${baseApiUrl}notifications/vapid-public-key`, {
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 401) {
      throw new Error('Sessão expirada. Faça login novamente.');
    }
    if (res.status === 503) {
      throw new Error(err.error || 'Notificações push não configuradas no servidor.');
    }
    throw new Error(err.error || err.message || 'Falha ao obter chave de notificações.');
  }
  const data = await res.json();
  const key = (data.vapid_public_key || '').trim();
  if (!key) throw new Error('Chave de notificações não retornada.');
  try {
    return urlBase64ToUint8Array(key);
  } catch (e) {
    throw new Error('Chave de notificações em formato inválido.');
  }
}

/**
 * Converte o objeto PushSubscription para o formato esperado pelo back-end.
 */
function subscriptionToRequestBody(subscription) {
  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: key ? btoa(String.fromCharCode.apply(null, new Uint8Array(key))) : '',
      auth: auth ? btoa(String.fromCharCode.apply(null, new Uint8Array(auth))) : '',
    },
    contentEncoding: 'aesgcm',
  };
}

/**
 * Verifica se o navegador suporta notificações e Service Worker.
 */
export function isPushSupported() {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

/**
 * Define se o modal de consentimento deve ser exibido no login.
 * Regra: só perguntar quando a permissão do navegador estiver "default" (ainda não respondida).
 */
export function shouldPromptPushConsent() {
  if (!isPushSupported()) {
    return false;
  }
  return Notification.permission === 'default';
}

/**
 * Aguarda o Service Worker estar pronto, com timeout.
 * Em desenvolvimento o SW pode não estar registrado; em produção (PWA) está.
 */
function getServiceWorkerReady(timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    const timeoutMsg = 'Service worker não disponível. Use a versão instalada do app (PWA) ou acesse por HTTPS para habilitar notificações.';
    const timer = setTimeout(() => {
      reject(new Error(timeoutMsg));
    }, timeoutMs);
    navigator.serviceWorker.ready
      .then((reg) => {
        clearTimeout(timer);
        resolve(reg);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err.message ? err : new Error(timeoutMsg));
      });
  });
}

/**
 * Remove a subscription no back-end (evita endpoint órfão ao trocar de dispositivo ou VAPID).
 */
async function deletePushSubscriptionFromBackend(endpoint) {
  const headers = authHeader();
  if (!headers.Authorization) return;
  await fetch(`${baseApiUrl}notifications/push-subscription`, {
    method: 'DELETE',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ endpoint }),
  });
}

async function getCurrentPushSubscription() {
  if (!isPushSupported()) {
    return null;
  }
  const registration = await getServiceWorkerReady();
  return registration.pushManager.getSubscription();
}

export async function getPushSubscriptionStatus() {
  const supported = isPushSupported();
  if (!supported) {
    return {
      supported: false,
      permission: 'unsupported',
      enabled: false,
    };
  }

  let enabled = false;
  if (Notification.permission === 'granted') {
    const subscription = await getCurrentPushSubscription().catch(() => null);
    enabled = !!subscription;
  }

  return {
    supported: true,
    permission: Notification.permission,
    enabled,
  };
}

export async function disablePushSubscription() {
  if (!isPushSupported()) {
    return false;
  }

  const subscription = await getCurrentPushSubscription();
  if (!subscription) {
    return true;
  }

  try {
    await deletePushSubscriptionFromBackend(subscription.endpoint);
  } catch (e) {
    console.warn('[WebPush] Falha ao remover subscription no backend:', e);
  }

  await subscription.unsubscribe();
  return true;
}

/**
 * Inscreve o usuário em Web Push e envia a subscription para o back-end.
 * Se já existir inscrição (ex.: de outra chave VAPID), faz unsubscribe antes de inscrever de novo,
 * evitando o erro "applicationServerKey (or gcm_sender_id) already exists" ao rotacionar chaves.
 * Requer que o usuário já esteja autenticado (token no cookie/header).
 * @returns {Promise<boolean>} true se inscrito e enviado com sucesso, false se negado ou erro.
 */
export async function subscribeAndSendToBackend() {
  if (!isPushSupported()) {
    throw new Error('Seu navegador não suporta notificações push.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return false;
  }

  try {
    const registration = await getServiceWorkerReady();
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      try {
        await deletePushSubscriptionFromBackend(existingSubscription.endpoint);
      } catch (e) {
        console.warn('[WebPush] Ignorando falha ao remover subscription antiga no servidor:', e);
      }
      await existingSubscription.unsubscribe();
    }

    const vapidPublicKey = await getVapidPublicKey();
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidPublicKey,
    });

    const body = subscriptionToRequestBody(subscription);
    const headers = authHeader();
    if (!headers.Authorization) {
      throw new Error('Sessão inválida. Faça login novamente.');
    }
    const res = await fetch(`${baseApiUrl}notifications/push-subscription`, {
      method: 'POST',
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const msg = err.message || err.error || `Erro ${res.status} ao registrar notificações.`;
      throw new Error(msg);
    }
    return true;
  } catch (e) {
    console.warn('[WebPush] Erro ao inscrever:', e);
    const msg = (e && e.message) ? String(e.message) : '';
    if (msg.includes('push service not available') || msg.includes('Registration failed')) {
      throw new Error('Serviço de notificações indisponível no momento. Tente novamente mais tarde ou em outro navegador/dispositivo.');
    }
    throw e;
  }
}
